<template>
  <div class="container my-4">
    <div class="row g-3">
      <div class="col-12 col-lg-4">
        <div class="card h-100 shadow-sm">
          <div class="card-header d-flex align-items-center justify-content-between">
            <span class="fw-bold">Profile Details</span>
            <div class="form-check form-switch m-0">
              <label class="form-check-label me-2" for="profileEditSwitch">Edit</label>
              <input
                id="profileEditSwitch"
                class="form-check-input"
                type="checkbox"
                role="switch"
                v-model="editMode"
              >
            </div>
          </div>
          <div class="card-body text-start">
            <div v-if="profileLoading" class="text-muted">Loading profile...</div>
            <template v-else>
              <div class="mb-2">
                <label class="form-label fw-bold">First Name</label>
                <input class="form-control" type="text" v-model="profile.fname" :disabled="!editMode">
              </div>
              <div class="mb-2">
                <label class="form-label fw-bold">Middle Name</label>
                <input class="form-control" type="text" v-model="profile.mname" :disabled="!editMode">
              </div>
              <div class="mb-2">
                <label class="form-label fw-bold">Last Name</label>
                <input class="form-control" type="text" v-model="profile.lname" :disabled="!editMode">
              </div>
              <div class="mb-2">
                <label class="form-label fw-bold">Username</label>
                <input class="form-control" type="text" v-model="profile.username" disabled>
              </div>
              <div class="mb-2">
                <label class="form-label fw-bold">Email</label>
                <input class="form-control" type="email" v-model="profile.email" :disabled="!editMode">
              </div>
              <div class="mb-2">
                <label class="form-label fw-bold">Phone</label>
                <input class="form-control" type="text" v-model="profile.phone" :disabled="!editMode">
              </div>
              <div class="mb-2" v-if="editMode">
                <label class="form-label fw-bold">New Password</label>
                <input class="form-control" type="password" v-model="newPassword" placeholder="Leave blank to keep unchanged">
              </div>
              <div class="mb-2" v-if="editMode">
                <label class="form-label fw-bold">Confirm Password</label>
                <input class="form-control" type="password" v-model="confirmPassword" placeholder="Repeat new password">
              </div>
            </template>
          </div>
          <div class="card-footer bg-white text-start">
            <button class="btn btn-primary" :disabled="!editMode || saving" @click.prevent="saveProfile">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
              Save Profile
            </button>
            <button class="btn btn-outline-secondary ms-2" :disabled="!editMode || saving" @click.prevent="resetProfileForm">
              Cancel
            </button>
          </div>
        </div>
      </div>

      <div class="col-12 col-lg-8">
        <div class="card h-100 shadow-sm">
          <div class="card-header d-flex align-items-center justify-content-between">
            <span class="fw-bold">Entity Summary</span>
            <button class="btn btn-sm btn-outline-primary" :disabled="statsLoading || !canViewEntitySummary" @click.prevent="loadEntityStats">Refresh</button>
          </div>
          <div class="card-body text-start">
            <div v-if="!canViewEntitySummary" class="alert alert-info py-2 mb-2">
              You do not have permission to view entity template/invoice summary.
            </div>
            <div v-else-if="statsLoading" class="text-muted">Loading entity stats...</div>
            <div v-else-if="statsError" class="alert alert-warning py-2 mb-2">{{ statsError }}</div>
            <div v-if="canViewEntitySummary" class="table-responsive">
              <table class="table table-sm table-striped align-middle mb-0">
                <thead>
                  <tr>
                    <th>Entity</th>
                    <th class="text-end">Templates</th>
                    <th class="text-end">Invoices</th>
                    <th class="text-end">Deleted Invoices</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!entityRows.length">
                    <td colspan="4" class="text-muted">No entities available.</td>
                  </tr>
                  <tr v-for="row in entityRows" :key="row.ent_id">
                    <td>{{ row.entity_name }}</td>
                    <td class="text-end">{{ row.templates_count }}</td>
                    <td class="text-end">{{ row.invoices_count }}</td>
                    <td class="text-end">{{ row.deleted_invoices_count }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-lg-6">
        <div class="card shadow-sm">
          <div class="card-header fw-bold">Backup and Restore</div>
          <div class="card-body text-start">
            <div v-if="!canUseBackupRestore" class="alert alert-warning py-2 mb-0">
              Backup/Restore is available only for User group members.
            </div>
            <template v-else>
              <ul class="nav nav-tabs mb-3">
                <li class="nav-item">
                  <button
                    class="nav-link"
                    :class="{ active: activeBackupTab === 'backup' }"
                    @click="activeBackupTab = 'backup'"
                    type="button"
                  >
                    Backup
                  </button>
                </li>
                <li class="nav-item">
                  <button
                    class="nav-link"
                    :class="{ active: activeBackupTab === 'restore' }"
                    @click="activeBackupTab = 'restore'"
                    type="button"
                  >
                    Restore
                  </button>
                </li>
              </ul>

              <div v-if="activeBackupTab === 'backup'">
                <p class="text-muted mb-3">
                  Download all your entities, master data, and invoice records as a JSON backup file.
                </p>
                <button class="btn btn-primary" :disabled="backupBusy" @click="downloadBackup">
                  <span v-if="backupBusy" class="spinner-border spinner-border-sm me-1"></span>
                  Download Backup JSON
                </button>
              </div>

              <div v-else>
                <p class="text-muted mb-2">
                  Restore from a previously exported backup JSON file.
                </p>
                <p class="text-danger small mb-3">
                  This replaces your current entities, masters, templates, and invoices for this user.
                </p>
                <div class="mb-2">
                  <input class="form-control" type="file" accept="application/json,.json" @change="onRestoreFilePicked">
                </div>
                <div class="small text-muted mb-3" v-if="restoreFileName">
                  Selected file: {{ restoreFileName }}
                </div>
                <div class="border rounded p-3 mb-3 bg-light" v-if="restorePayload">
                  <div class="fw-bold mb-2">Restore Preview</div>
                  <div class="small mb-2">
                    <span class="me-3">Schema version: <strong>{{ restoreSchemaVersion }}</strong></span>
                    <span>
                      payee_entities key:
                      <span v-if="hasPayeeEntitiesKey" class="text-success fw-bold">present</span>
                      <span v-else class="text-warning fw-bold">missing (legacy fallback: p_entity)</span>
                    </span>
                  </div>
                  <div class="small text-muted mb-2">
                    Import order: Entities -> Payee Entities -> Taxes -> Banks -> Branches -> Bank Accounts -> Billables -> Invoice Templates -> Invoices
                  </div>
                  <div class="alert alert-secondary py-2 mb-2">
                    Incoming backup volume: <strong>{{ restoreTotalRecords }}</strong> total records across core tables.
                  </div>
                  <div v-if="restoreIsLarge" class="alert alert-warning py-2 mb-2">
                    Large restore detected. Review the table counts carefully before confirming.
                  </div>
                  <div class="table-responsive mb-2">
                    <table class="table table-sm table-striped mb-0">
                      <thead>
                        <tr>
                          <th>Table</th>
                          <th class="text-end">Records</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="row in restorePreviewRows"
                          :key="row.key"
                          :class="{ 'table-warning': row.isLarge }"
                        >
                          <td>{{ row.label }}</td>
                          <td class="text-end">
                            {{ row.count }}
                            <span v-if="row.isLarge" class="badge text-bg-warning ms-1">large</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div class="form-check">
                    <input
                      id="restoreConfirmCheck"
                      class="form-check-input"
                      type="checkbox"
                      v-model="restoreConfirmed"
                    >
                    <label class="form-check-label small" for="restoreConfirmCheck">
                      I understand this will replace my current data and cannot be undone.
                    </label>
                  </div>
                </div>
                <button class="btn btn-danger" :disabled="restoreBusy || !restorePayload || !restoreConfirmed" @click="restoreBackup">
                  <span v-if="restoreBusy" class="spinner-border spinner-border-sm me-1"></span>
                  Restore Data
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProfileSettingsView',
  data() {
    return {
      profileLoading: false,
      saving: false,
      statsLoading: false,
      editMode: false,
      profile: {
        id: null,
        fname: '',
        mname: '',
        lname: '',
        username: '',
        email: '',
        phone: '',
        gender: 'unspecified',
        status: ''
      },
      originalProfile: null,
      newPassword: '',
      confirmPassword: '',
      entityRows: [],
      statsError: '',
      activeBackupTab: 'backup',
      backupBusy: false,
      restoreBusy: false,
      restorePayload: null,
      restoreFileName: '',
      restoreConfirmed: false
    };
  },
  computed: {
    entities() {
      return this.$store.getters.getUser?.entities || [];
    },
    canViewEntitySummary() {
      const acl = Array.isArray(this.$store.getters.getUser?.acl) ? this.$store.getters.getUser.acl : [];
      const canReadModule = (moduleName) => acl.some((entry) => {
        if (entry?.name !== moduleName) return false;
        return !!entry?.GroupModules?.read;
      });

      return this.$store.getters.isUserGroupMember && canReadModule('Invoice Template') && canReadModule('View Invoice');
    },
    canUseBackupRestore() {
      return this.$store.getters.isUserGroupMember && !this.$store.getters.isAdmin;
    },
    restorePreviewRows() {
      const tables = this.restorePayload?.tables || {};
      const getCount = (key) => (Array.isArray(tables[key]) ? tables[key].length : 0);
      const getPayeeCount = () => {
        const primary = getCount('payee_entities');
        if (primary > 0) return primary;
        return getCount('p_entity');
      };
      const markLarge = (count) => Number(count) >= 500;

      return [
        { key: 'entity', label: 'Entities', count: getCount('entity'), isLarge: markLarge(getCount('entity')) },
        { key: 'payee_entities', label: 'Payee Entities', count: getPayeeCount(), isLarge: markLarge(getPayeeCount()) },
        { key: 'tax', label: 'Taxes', count: getCount('tax'), isLarge: markLarge(getCount('tax')) },
        { key: 'banks', label: 'Banks', count: getCount('banks'), isLarge: markLarge(getCount('banks')) },
        { key: 'bank_branch', label: 'Branches', count: getCount('bank_branch'), isLarge: markLarge(getCount('bank_branch')) },
        { key: 'bank_accounts', label: 'Bank Accounts', count: getCount('bank_accounts'), isLarge: markLarge(getCount('bank_accounts')) },
        { key: 'billables', label: 'Billables', count: getCount('billables'), isLarge: markLarge(getCount('billables')) },
        { key: 'invoice_template', label: 'Invoice Templates', count: getCount('invoice_template'), isLarge: markLarge(getCount('invoice_template')) },
        { key: 'invoices', label: 'Invoices', count: getCount('invoices'), isLarge: markLarge(getCount('invoices')) }
      ];
    },
    restoreTotalRecords() {
      return this.restorePreviewRows.reduce((sum, row) => sum + Number(row.count || 0), 0);
    },
    restoreIsLarge() {
      return this.restorePreviewRows.some((row) => row.isLarge) || this.restoreTotalRecords >= 2000;
    },
    restoreSchemaVersion() {
      const version = this.restorePayload?.meta?.format_version;
      return version == null ? 'unknown' : String(version);
    },
    hasPayeeEntitiesKey() {
      const tables = this.restorePayload?.tables;
      return !!tables && Object.prototype.hasOwnProperty.call(tables, 'payee_entities');
    }
  },
  mounted() {
    this.loadProfile();
    if (this.canViewEntitySummary) {
      this.loadEntityStats();
    }
  },
  methods: {
    normalizeProfile(data) {
      return {
        id: data?.id || null,
        fname: String(data?.fname || ''),
        mname: String(data?.mname || ''),
        lname: String(data?.lname || ''),
        username: String(data?.username || ''),
        email: String(data?.email || ''),
        phone: String(data?.phone || ''),
        gender: String(data?.gender || 'unspecified'),
        status: String(data?.status || '')
      };
    },
    async loadProfile() {
      this.profileLoading = true;
      try {
        const response = await this.$store.getters.getAxios.post('/user/readmyprofile', {});
        const loaded = this.normalizeProfile(response?.data?.data || {});
        this.profile = loaded;
        this.originalProfile = { ...loaded };
      } catch (err) {
        this.$store.commit('addAlert', {
          title: 'Profile Error',
          message: err?.response?.data?.msg || 'Unable to load profile',
          type: 'danger'
        });
      } finally {
        this.profileLoading = false;
      }
    },
    resetProfileForm() {
      if (this.originalProfile) {
        this.profile = { ...this.originalProfile };
      }
      this.newPassword = '';
      this.confirmPassword = '';
      this.editMode = false;
    },
    async saveProfile() {
      if (!this.editMode) return;

      if (!String(this.profile.fname || '').trim() || !String(this.profile.lname || '').trim()) {
        this.$store.commit('addAlert', {
          title: 'Validation Error',
          message: 'First name and last name are required.',
          type: 'danger'
        });
        return;
      }

      if (!String(this.profile.email || '').trim() || !String(this.profile.phone || '').trim()) {
        this.$store.commit('addAlert', {
          title: 'Validation Error',
          message: 'Email and phone are required.',
          type: 'danger'
        });
        return;
      }

      const hasPassword = !!String(this.newPassword || '').length;
      if (hasPassword && String(this.newPassword).length < 8) {
        this.$store.commit('addAlert', {
          title: 'Validation Error',
          message: 'Password must be at least 8 characters.',
          type: 'danger'
        });
        return;
      }

      if (hasPassword && this.newPassword !== this.confirmPassword) {
        this.$store.commit('addAlert', {
          title: 'Validation Error',
          message: 'Password confirmation does not match.',
          type: 'danger'
        });
        return;
      }

      this.saving = true;
      try {
        const payload = {
          fname: String(this.profile.fname || '').trim(),
          mname: String(this.profile.mname || '').trim(),
          lname: String(this.profile.lname || '').trim(),
          email: String(this.profile.email || '').trim(),
          phone: String(this.profile.phone || '').trim(),
          gender: String(this.profile.gender || 'unspecified').trim()
        };

        if (hasPassword) {
          payload.password = this.newPassword;
        }

        const response = await this.$store.getters.getAxios.put('/user/updatemyprofile', payload);
        const saved = this.normalizeProfile(response?.data?.data || payload);
        this.profile = saved;
        this.originalProfile = { ...saved };
        this.newPassword = '';
        this.confirmPassword = '';
        this.editMode = false;

        const currentUser = this.$store.getters.getUser || {};
        this.$store.commit('setUser', {
          ...currentUser,
          name: saved.fname
        });

        this.$store.commit('addAlert', {
          title: response?.data?.title || 'Profile Updated',
          message: response?.data?.msg || 'Profile updated successfully.',
          type: 'success'
        });
      } catch (err) {
        this.$store.commit('addAlert', {
          title: 'Profile Error',
          message: err?.response?.data?.msg || 'Unable to update profile',
          type: 'danger'
        });
      } finally {
        this.saving = false;
      }
    },
    async loadEntityStats() {
      if (!this.canViewEntitySummary) {
        this.entityRows = [];
        this.statsError = '';
        return;
      }

      this.statsLoading = true;
      this.statsError = '';

      try {
        const entityList = Array.isArray(this.entities) ? this.entities : [];
        const templateCounts = {};
        const invoiceCounts = {};

        await Promise.all(entityList.map(async (entity) => {
          try {
            const response = await this.$store.getters.getAxios.post('/invtemp/getinvtempnames', { ent_id: entity.id });
            templateCounts[entity.id] = Array.isArray(response?.data?.data) ? response.data.data.length : 0;
          } catch (err) {
            templateCounts[entity.id] = 0;
          }
        }));

        try {
          const response = await this.$store.getters.getAxios.post('/invoice/entitycounts', {
            ent_ids: entityList.map((entity) => entity.id)
          });
          const rows = Array.isArray(response?.data?.data?.rows) ? response.data.data.rows : [];
          rows.forEach((row) => {
            const entId = Number(row?.ent_id);
            invoiceCounts[entId] = {
              invoices_count: Number(row?.active_count || 0),
              deleted_invoices_count: Number(row?.deleted_count || 0)
            };
          });
        } catch (err) {
          this.statsError = err?.response?.data?.msg || 'Invoice counts could not be loaded for this user.';
        }

        this.entityRows = entityList.map((entity) => {
          const invoiceData = invoiceCounts[entity.id] || { invoices_count: 0, deleted_invoices_count: 0 };
          return {
            ent_id: entity.id,
            entity_name: entity.name,
            templates_count: Number(templateCounts[entity.id] || 0),
            invoices_count: invoiceData.invoices_count,
            deleted_invoices_count: invoiceData.deleted_invoices_count
          };
        });
      } finally {
        this.statsLoading = false;
      }
    },
    async downloadBackup() {
      if (!this.canUseBackupRestore || this.backupBusy) return;

      this.backupBusy = true;
      try {
        const response = await this.$store.getters.getAxios.get('/backup/export');
        const payload = response?.data?.data || {};
        const exportedAt = payload?.meta?.exported_at || new Date().toISOString();
        const safeTs = String(exportedAt).replace(/[.:]/g, '-');
        const fileName = `pavti-backup-${safeTs}.json`;

        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);

        this.$store.commit('addAlert', {
          title: response?.data?.title || 'Backup Generated',
          message: response?.data?.msg || 'Backup JSON downloaded successfully.',
          type: 'success'
        });
      } catch (err) {
        this.$store.commit('addAlert', {
          title: 'Backup Error',
          message: err?.response?.data?.msg || 'Unable to generate backup.',
          type: 'danger'
        });
      } finally {
        this.backupBusy = false;
      }
    },
    async onRestoreFilePicked(event) {
      const file = event?.target?.files?.[0];
      this.restorePayload = null;
      this.restoreFileName = file?.name || '';
      this.restoreConfirmed = false;

      if (!file) return;

      try {
        const text = await file.text();
        const parsed = JSON.parse(text);

        if (!parsed || typeof parsed !== 'object' || !parsed.tables || typeof parsed.tables !== 'object') {
          throw new Error('Invalid backup JSON structure.');
        }

        this.restorePayload = parsed;
      } catch (err) {
        this.restorePayload = null;
        this.restoreConfirmed = false;
        this.$store.commit('addAlert', {
          title: 'Invalid Backup File',
          message: err?.message || 'Selected file is not a valid backup JSON.',
          type: 'danger'
        });
      }
    },
    async restoreBackup() {
      if (!this.canUseBackupRestore || this.restoreBusy || !this.restorePayload) return;

      if (!this.restoreConfirmed) {
        this.$store.commit('addAlert', {
          title: 'Confirmation Required',
          message: 'Review the restore preview and confirm before restoring.',
          type: 'warning'
        });
        return;
      }

      this.restoreBusy = true;
      try {
        const response = await this.$store.getters.getAxios.post('/backup/restore', this.restorePayload);

        this.$store.commit('addAlert', {
          title: response?.data?.title || 'Restore Completed',
          message: response?.data?.msg || 'Backup restored successfully.',
          type: 'success'
        });

        this.restorePayload = null;
        this.restoreFileName = '';
        this.restoreConfirmed = false;
        this.activeBackupTab = 'backup';

        await this.loadEntityStats();
      } catch (err) {
        this.$store.commit('addAlert', {
          title: 'Restore Error',
          message: err?.response?.data?.msg || 'Restore failed.',
          type: 'danger'
        });
      } finally {
        this.restoreBusy = false;
      }
    }
  }
};
</script>
